import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { getApiUrl } from '../../config/api';
import type { ScannerData } from '../../types/ScannerData';
import type { OptionRow } from '../../services/gexCalculator';
import { buildMarketAnalyticsSnapshot } from '../../utils/marketRegimeEngine';
import { regimeMeta, RISK_LEVEL_COLORS } from '../../utils/regimeTheme';
import { isDesignSystemOn } from '../../theme/mmecDsPreview';

// ── Constants ──────────────────────────────────────────────────────────────
const US_RTH_MINUTES = 390;
const SESSIONS_PER_YEAR = 252;

// ── Helpers ────────────────────────────────────────────────────────────────
function fmtPrice(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmt(n: number, d = 2): string {
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
}
function safeNum(v: unknown, fb = 0): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fb;
}

function getETTime(d: Date): { h: number; m: number; s: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).formatToParts(d);
  return {
    h: parseInt(parts.find((p) => p.type === 'hour')?.value ?? '0', 10) || 0,
    m: parseInt(parts.find((p) => p.type === 'minute')?.value ?? '0', 10) || 0,
    s: parseInt(parts.find((p) => p.type === 'second')?.value ?? '0', 10) || 0,
  };
}

function isUSWeekday(d: Date): boolean {
  const wd = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', weekday: 'short' }).format(d);
  return wd !== 'Sat' && wd !== 'Sun';
}

function rthFractionRemaining(now: Date): number {
  if (!isUSWeekday(now)) return 0;
  const { h, m } = getETTime(now);
  const mins = h * 60 + m;
  const open = 9 * 60 + 30;
  const close = 16 * 60;
  if (mins < open) return 1;
  if (mins >= close) return 0;
  return (close - mins) / US_RTH_MINUTES;
}

function timeUntilClose(now: Date): { display: string; sub: string } {
  if (!isUSWeekday(now)) return { display: 'Closed', sub: 'Market Closed' };
  const { h, m, s } = getETTime(now);
  const mins = h * 60 + m;
  const close = 16 * 60;
  if (mins >= close) return { display: 'Closed', sub: 'Market Closed' };
  if (mins < 9 * 60 + 30) return { display: 'Pre-Mkt', sub: 'Pre-Market' };
  const remaining = close - mins;
  const hrs = Math.floor(remaining / 60);
  const minsLeft = remaining % 60;
  return {
    display: `${String(hrs).padStart(2, '0')}:${String(minsLeft).padStart(2, '0')}:${String(s).padStart(2, '0')}`,
    sub: 'Until 16:00 ET',
  };
}

function formatETTime(d: Date): string {
  const { h, m, s } = getETTime(d);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')} ET`;
}

// ── Chain analysis ─────────────────────────────────────────────────────────
type ChainPick = {
  strike: number; gamma: number; delta: number; theta: number; vega: number;
  iv: number; dte: number; optionType: string; underlying: string; underlyingPrice: number;
};

type WallLevel = { strike: number; oi: number; type: 'call' | 'put' };

function findChainPick(rows: ScannerData[]): ChainPick | null {
  const valid = rows.filter(
    (r) => safeNum(r.Gamma ?? r.gamma) > 0 && safeNum(r.Delta ?? r.delta) !== 0
      && (safeNum(r['Implied Volatility']) > 0 || safeNum(r.iv) > 0),
  );
  if (!valid.length) return null;
  const withPrice = valid.filter((r) => safeNum(r['Underlying Price'] ?? r.underlying_price) > 0);
  const refPrice = withPrice.length ? safeNum(withPrice[0]['Underlying Price'] ?? withPrice[0].underlying_price) : 0;
  const sorted = refPrice > 0
    ? [...valid].sort((a, b) => Math.abs(safeNum(a.Strike) - refPrice) - Math.abs(safeNum(b.Strike) - refPrice))
    : valid;
  const r = sorted[0];
  return {
    strike: safeNum(r.Strike),
    gamma: safeNum(r.Gamma ?? r.gamma),
    delta: safeNum(r.Delta ?? r.delta),
    theta: safeNum(r.Theta ?? r.theta),
    vega: safeNum(r.Vega ?? r.vega),
    iv: safeNum(r['Implied Volatility'] ?? r.iv),
    dte: safeNum(r['Days left'] ?? r.days_left),
    optionType: r['Option Type'] ?? r.option_type ?? 'C',
    underlying: r.Underlying || r.underlyingSymbol || '',
    underlyingPrice: refPrice,
  };
}

function findWalls(rows: ScannerData[], spot: number): { calls: WallLevel[]; puts: WallLevel[]; gammaFlip: number | null } {
  const calls: WallLevel[] = [];
  const puts: WallLevel[] = [];
  for (const r of rows) {
    const oi = safeNum(r['Open Interest']);
    const strike = safeNum(r.Strike);
    const optType = (r['Option Type'] ?? r.option_type ?? '').toUpperCase();
    if (oi <= 0 || !Number.isFinite(strike)) continue;
    if (optType === 'C') calls.push({ strike, oi, type: 'call' });
    else if (optType === 'P') puts.push({ strike, oi, type: 'put' });
  }
  const callWalls = calls.filter((w) => w.strike >= spot).sort((a, b) => b.oi - a.oi).slice(0, 3);
  const putWalls = puts.filter((w) => w.strike <= spot).sort((a, b) => b.oi - a.oi).slice(0, 3);

  const allStrikesArr: number[] = [];
  const seen = new Set<number>();
  for (const r of rows) {
    const s = safeNum(r.Strike);
    if (Number.isFinite(s) && !seen.has(s)) { seen.add(s); allStrikesArr.push(s); }
  }
  let gammaFlip: number | null = null;
  let maxOi = 0;
  for (const s of allStrikesArr) {
    const cOi = calls.filter((c) => c.strike === s).reduce((sum, c) => sum + c.oi, 0);
    const pOi = puts.filter((p) => p.strike === s).reduce((sum, p) => sum + p.oi, 0);
    if (cOi + pOi > maxOi) { maxOi = cOi + pOi; gammaFlip = s; }
  }
  return { calls: callWalls, puts: putWalls, gammaFlip };
}

function normalCDF(z: number): number {
  const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741, a4 = -1.453152027, a5 = 1.061405429;
  const p = 0.3275911;
  const sign = z < 0 ? -1 : 1;
  const x = Math.abs(z) / Math.sqrt(2);
  const t = 1 / (1 + p * x);
  const y = 1 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
  return 0.5 * (1 + sign * y);
}

// ── Circular Progress Ring ─────────────────────────────────────────────────
function ProgressRing({ percent, size = 100, stroke = 6 }: { percent: number; size?: number; stroke?: number }) {
  // Design System preview: the ring is the one gold accent on a neutral track (SVG attributes cannot read CSS vars).
  const ds = isDesignSystemOn();
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={ds ? '#2a2e39' : 'rgba(139,92,246,0.15)'} strokeWidth={stroke} />
      <circle
        cx={size / 2} cy={size / 2} r={radius} fill="none"
        stroke="url(#ringGrad)" strokeWidth={stroke} strokeLinecap="round"
        strokeDasharray={circumference} strokeDashoffset={offset}
        style={{ transition: 'stroke-dashoffset 0.6s ease' }}
      />
      <defs>
        <linearGradient id="ringGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={ds ? '#ffb74d' : '#8B5CF6'} />
          <stop offset="100%" stopColor={ds ? '#ffb74d' : '#3B82F6'} />
        </linearGradient>
      </defs>
    </svg>
  );
}

// ── Component ──────────────────────────────────────────────────────────────
export interface SigmaRangeCalculatorProps {
  scannerRows?: ScannerData[];
  underlying?: string;
}

const SigmaRangeCalculator: React.FC<SigmaRangeCalculatorProps> = ({ scannerRows, underlying = 'SPX' }) => {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const ds = isDesignSystemOn();

  // ── Live clock (1s tick) ─────────────────────────────────────────────────
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const rthLeft = rthFractionRemaining(now);
  const isClosed = rthLeft <= 0;
  const timeInfo = timeUntilClose(now);

  // ── Chain data ───────────────────────────────────────────────────────────
  const chainPick = useMemo(() => scannerRows?.length ? findChainPick(scannerRows) : null, [scannerRows]);
  const walls = useMemo(() => {
    if (!scannerRows?.length || !chainPick) return { calls: [], puts: [], gammaFlip: null };
    return findWalls(scannerRows, chainPick.underlyingPrice);
  }, [scannerRows, chainPick]);

  // ── Inputs ───────────────────────────────────────────────────────────────
  const [spot, setSpot] = useState(0);
  const [ivPct, setIvPct] = useState(0);
  const [gamma, setGamma] = useState(0);
  const [delta, setDelta] = useState(0);
  const [theta, setTheta] = useState(0);
  const [vega, setVega] = useState(0);
  const [dte, setDte] = useState(0);
  const [refPrice, setRefPrice] = useState(0);
  const [lastClose, setLastClose] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  const [moveInput, setMoveInput] = useState(0);

  useEffect(() => {
    if (!chainPick) return;
    if (spot === 0 && chainPick.underlyingPrice > 0) { setSpot(chainPick.underlyingPrice); setMoveInput(chainPick.underlyingPrice); }
    if (ivPct === 0 && chainPick.iv > 0) setIvPct(chainPick.iv);
    if (gamma === 0 && chainPick.gamma > 0) setGamma(chainPick.gamma);
    if (delta === 0 && chainPick.delta !== 0) setDelta(chainPick.delta);
    if (theta === 0 && chainPick.theta !== 0) setTheta(chainPick.theta);
    if (vega === 0 && chainPick.vega !== 0) setVega(chainPick.vega);
    if (dte === 0 && chainPick.dte > 0) setDte(chainPick.dte);
    if (refPrice === 0 && chainPick.strike > 0) setRefPrice(chainPick.strike);
    if (lastClose === 0 && chainPick.underlyingPrice > 0) setLastClose(chainPick.underlyingPrice);
  }, [chainPick]); // eslint-disable-line

  // ── Sigma math ───────────────────────────────────────────────────────────
  const sigma = ivPct > 1 ? ivPct / 100 : ivPct;
  const move = (sessions: number) => spot * sigma * Math.sqrt(sessions / SESSIONS_PER_YEAR);
  const effective0dte = isClosed ? 1 / US_RTH_MINUTES : rthLeft;
  const move0dte = move(effective0dte);
  const move1d = move(1);
  const move1w = move(5);
  const move1m = move(21);
  const spxChange = lastClose > 0 ? spot - lastClose : 0;
  const spxChangePct = lastClose > 0 ? (spxChange / lastClose) * 100 : 0;

  // Auto-set move input to 1σ
  useEffect(() => { if (move0dte > 0 && moveInput === spot) setMoveInput(move0dte); }, [move0dte]); // eslint-disable-line

  // ── Sigma levels ─────────────────────────────────────────────────────────
  const sigmaLevels = useMemo(() => {
    if (spot <= 0 || move0dte <= 0) return [];
    return [3, 2, 1, 0, -1, -2, -3].map((n) => {
      const levelPrice = spot + n * move0dte;
      const distance = n * move0dte;
      const z = Math.abs(n);
      const prob = (1 - normalCDF(z)) * 200; // two-sided %
      return { n, levelPrice, distance, prob, isCurrent: n === 0 };
    });
  }, [spot, move0dte]);

  // ── Gamma impact ─────────────────────────────────────────────────────────
  const gi = (m: number) => ({
    deltaChange: gamma * m,
    newDelta: delta + gamma * m,
    priceImpact: 0.5 * gamma * m * m,
  });

  // ── Market Regime (from chain data) ──────────────────────────────────────
  const regimeSnapshot = useMemo(() => {
    if (!scannerRows?.length || spot <= 0) return null;
    try {
      return buildMarketAnalyticsSnapshot(underlying.toUpperCase(), scannerRows as OptionRow[], null);
    } catch { return null; }
  }, [scannerRows, underlying, spot]);

  const regime = regimeSnapshot ? regimeMeta(regimeSnapshot.regime) : null;
  const rnd = regimeSnapshot?.rnd;

  // ── Credit Hunter (backend API) ──────────────────────────────────────────
  const [creditData, setCreditData] = useState<{
    bestVertical: { short: number | null; long: number | null; expiry: string | null; rationale: string } | null;
    top3: { strike: number; score: number }[];
    context: string | null;
    loading: boolean;
  }>({ bestVertical: null, top3: [], context: null, loading: false });

  const fetchCreditHunter = useCallback(async () => {
    setCreditData((prev) => ({ ...prev, loading: true }));
    try {
      const res = await fetch(getApiUrl('/api/smart-flow/gamma-credit-hunter/scan'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ underlying, min_oi: 100, min_gamma: 0 }),
      });
      const json = await res.json();
      if (json.success) {
        setCreditData({
          bestVertical: json.best_credit_vertical ? {
            short: json.best_credit_vertical.short_strike,
            long: json.best_credit_vertical.long_strike,
            expiry: json.best_credit_vertical.expiry,
            rationale: json.best_credit_vertical.rationale || '',
          } : null,
          top3: (json.weekly_top_3 || []).map((w: { strike: number; weekly_strength: number }) => ({ strike: w.strike, score: w.weekly_strength })),
          context: json.context_line || null,
          loading: false,
        });
      } else {
        setCreditData((prev) => ({ ...prev, loading: false }));
      }
    } catch {
      setCreditData((prev) => ({ ...prev, loading: false }));
    }
  }, [underlying]);

  // ── Design tokens: surfaces/text/market colours from the platform palette; the purple/blue
  //    stat-tile accent system stays as this tool's own signature ────────────────────────────
  const c = {
    bg: 'var(--base)',
    card: 'var(--elev)',
    cardAlt: 'var(--elev2)',
    border: 'var(--hair)',
    text: 'var(--txt)',
    textSecondary: 'var(--txt-2)',
    textMuted: 'var(--muted)',
    green: 'var(--up)',
    greenDim: 'var(--up-dim)',
    red: 'var(--down)',
    redDim: 'var(--down-dim)',
    purple: 'var(--gold, #8B5CF6)',
    purpleDim: 'var(--gold-wash, rgba(139,92,246,0.12))',
    blue: 'var(--info, #3B82F6)',
    blueDim: 'var(--info-wash, rgba(59,130,246,0.12))',
    yellow: 'var(--warn)',
    yellowDim: 'var(--warn-dim)',
    orange: '#F59E0B',
    orangeDim: 'rgba(245,158,11,0.12)',
    cyan: '#06B6D4',
    // Design System levels: gamma flip blue, walls magenta (call solid, put dashed); members keep purple/green/red.
    levelKey: 'var(--level-key, #8B5CF6)',
    levelKeyDim: 'var(--surface-2, rgba(139,92,246,0.12))',
    callWall: 'var(--level-cw-pw, var(--up))',
    callWallDim: 'var(--surface-2, var(--up-dim))',
    putWall: 'var(--level-cw-pw, var(--down))',
    putWallDim: 'var(--surface-2, var(--down-dim))',
    // Non-directional emphasis (clock, scores, greeks): plain text under the system.
    plain: ds ? 'var(--text)' : '',
  };
  const wallEdge = (kind: 'call' | 'put'): React.CSSProperties =>
    ds ? { borderInlineStart: `2px ${kind === 'call' ? 'solid' : 'dashed'} var(--level-cw-pw)` } : {};

  // ── Shared styles ────────────────────────────────────────────────────────
  const card: React.CSSProperties = {
    backgroundColor: c.card, border: `1px solid ${c.border}`, borderRadius: 'var(--radius-md, 10px)', padding: 16,
  };
  const label: React.CSSProperties = {
    fontSize: 10.5, fontWeight: 600, textTransform: ds && isAr ? 'none' : 'uppercase', letterSpacing: ds && isAr ? 0 : '0.08em', color: 'var(--text-muted, var(--muted-2))',
  };
  const th: React.CSSProperties = {
    padding: '6px 10px', fontSize: 11, fontWeight: 600, letterSpacing: '0.03em',
    color: c.textMuted, textAlign: 'center', borderBottom: `1px solid ${c.border}`,
  };
  const td: React.CSSProperties = {
    padding: '6px 10px', fontSize: 12.5, textAlign: 'center', fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-mono)',
    borderBottom: `1px solid ${c.border}`, color: c.text,
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: 16, paddingBottom: ds ? 64 : 16, backgroundColor: c.bg, minHeight: '100vh', color: c.text, fontFamily: 'var(--font-ui)' }}>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* HEADER BAR                                                         */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', backgroundColor: c.card, border: `1px solid ${c.border}`, borderRadius: 'var(--radius-md, 10px)', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Logo */}
          <div style={{ width: 32, height: 32, borderRadius: 6, backgroundColor: c.purple, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M2 14L6 8L10 11L16 3" stroke={ds ? '#1a1406' : 'white'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 600, color: c.text, lineHeight: 1.2 }}>SPX Sigma & Options Levels</div>
            <div style={{ fontSize: 11, color: c.textMuted }}>Live Summary · <span style={{ color: c.purple }}>by Abu Abdulla</span></div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          {/* LIVE pill */}
          {/* Design System: live status = neutral pill with the up-green dot only (rule 4). */}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 20, backgroundColor: ds ? 'var(--surface-2)' : c.greenDim, fontSize: 10, fontWeight: 700, color: ds ? 'var(--text-axis)' : c.green }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: c.green, animation: 'pulse 2s infinite' }} />
            LIVE
          </span>
          <span style={{ fontSize: 12, color: c.plain || c.yellow, fontWeight: 600, fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-mono)' }}>{formatETTime(now)}</span>
          <button onClick={() => setShowSettings(!showSettings)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: c.textMuted, padding: 4 }} title={isAr ? 'الإعدادات' : 'Settings'}>⚙</button>
        </div>
      </div>

      {/* Pulse animation */}
      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* TOP METRICS STRIP (5 cards)                                        */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginBottom: 12 }}>
        {/* 1: SPX Price */}
        <div style={card}>
          <div style={label}>{isAr ? 'سعر SPX' : 'SPX PRICE'}</div>
          <div style={{ fontSize: 22, fontWeight: 600, fontFamily: 'var(--font-mono)', color: spxChange >= 0 ? c.green : c.red, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{fmtPrice(spot)}</div>
          <div style={{ fontSize: 12, fontWeight: 600, color: spxChange >= 0 ? c.green : c.red, marginTop: 2 }}>
            {spxChange >= 0 ? '▲' : '▼'} {fmtPrice(Math.abs(spxChange))} ({spxChangePct >= 0 ? '+' : ''}{spxChangePct.toFixed(2)}%)
          </div>
        </div>

        {/* 2: Daily σ */}
        <div style={card}>
          <div style={label}>{isAr ? 'الانحراف اليومي' : 'DAILY σ (1σ)'}</div>
          <div style={{ fontSize: 22, fontWeight: 600, fontFamily: 'var(--font-mono)', color: c.purple, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{fmtPrice(move1d)}</div>
          <div style={{ fontSize: 12, color: c.textMuted, marginTop: 2 }}>IV: {fmt(ivPct, 1)}%</div>
        </div>

        {/* 3: Reference Price */}
        <div style={card}>
          <div style={label}>{isAr ? 'سعر المرجع' : 'REFERENCE PRICE'}</div>
          <div style={{ fontSize: 22, fontWeight: 600, fontFamily: 'var(--font-mono)', color: c.text, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{fmtPrice(refPrice)}</div>
          <div style={{ fontSize: 12, color: c.textMuted, marginTop: 2 }}>{isAr ? 'آخر إغلاق' : 'Last Close'}: {fmtPrice(lastClose)}</div>
        </div>

        {/* 4: DTE */}
        <div style={card}>
          <div style={label}>{isAr ? 'أيام الانتهاء' : 'DTE (SELECTED)'}</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 4 }}>
            <input
              type="number" min={0} value={dte}
              onChange={(e) => setDte(Math.max(0, safeNum(e.target.value)))}
              style={{ width: 56, background: 'none', border: 'none', fontSize: 22, fontWeight: 600, fontFamily: 'var(--font-mono)', color: c.blue, fontVariantNumeric: 'tabular-nums', outline: 'none', padding: 0 }}
            />
            <span style={{ fontSize: 12, color: c.textMuted }}>{dte === 0 ? 'Intraday' : dte === 1 ? 'Day' : 'Days'}</span>
          </div>
        </div>

        {/* 5: Time Remaining */}
        <div style={card}>
          <div style={label}>{isAr ? 'الوقت المتبقي' : 'TIME REMAINING'}</div>
          <div style={{ fontSize: 22, fontWeight: 600, fontFamily: 'var(--font-mono)', color: isClosed ? (ds ? c.textMuted : c.red) : (c.plain || c.yellow), marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{timeInfo.display}</div>
          <div style={{ fontSize: 12, color: c.textMuted, marginTop: 2 }}>{timeInfo.sub}</div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* SETTINGS PANEL (collapsible)                                       */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {showSettings && (
        <div style={{ ...card, marginBottom: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10 }}>
          {[
            { l: 'Spot', v: spot, s: setSpot },
            { l: 'IV %', v: ivPct, s: setIvPct },
            { l: 'Gamma', v: gamma, s: setGamma },
            { l: 'Delta', v: delta, s: setDelta },
            { l: 'Theta', v: theta, s: setTheta },
            { l: 'Vega', v: vega, s: setVega },
            { l: 'Ref Price', v: refPrice, s: setRefPrice },
            { l: 'Last Close', v: lastClose, s: setLastClose },
          ].map(({ l, v, s }) => (
            <div key={l}>
              <div style={{ ...label, marginBottom: 4 }}>{l}</div>
              <input type="number" step="any" value={v || ''} onChange={(e) => s(safeNum(e.target.value))}
                style={{ width: '100%', padding: '5px 8px', borderRadius: ds ? 4 : 6, border: `1px solid ${c.border}`, backgroundColor: c.cardAlt, color: c.text, fontSize: 13, fontVariantNumeric: 'tabular-nums', outline: 'none' }} />
            </div>
          ))}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* MAIN CONTENT: 70/30 SPLIT                                          */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 12 }}>

        {/* ── LEFT: Sigma Levels Table ──────────────────────────────────── */}
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 14 }}>
            <h2 style={{ fontSize: 14, fontWeight: 600, color: c.text, margin: 0 }}>
              Sigma Levels & Option Strikes
            </h2>
            <span style={{ width: 16, height: 16, borderRadius: '50%', border: `1px solid ${c.textMuted}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: c.textMuted, cursor: 'default' }}>i</span>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...th, textAlign: 'left' }}>Puts (Below)</th>
                  <th style={th}>Level</th>
                  <th style={th}>Distance (pts)</th>
                  <th style={{ ...th, textAlign: 'right' }}>Calls (Above)</th>
                  <th style={th}>Probability (±)</th>
                </tr>
              </thead>
              <tbody>
                {sigmaLevels.map((lv) => {
                  const is1sigma = Math.abs(lv.n) === 1;
                  const isCurrent = lv.isCurrent;
                  const bg = isCurrent
                    ? 'var(--info-wash, rgba(59,130,246,0.08))'
                    : is1sigma
                      ? 'var(--gold-wash, rgba(139,92,246,0.06))'
                      : undefined;

                  return (
                    <tr key={lv.n} style={{ backgroundColor: bg }}>
                      <td style={{ ...td, textAlign: 'left', color: lv.n < 0 ? c.red : c.textMuted, fontWeight: lv.n < 0 ? 600 : 400 }}>
                        {lv.n < 0 ? fmtPrice(lv.levelPrice) : ''}
                      </td>
                      <td style={{
                        ...td,
                        fontWeight: isCurrent ? 700 : 600,
                        fontSize: isCurrent ? 14 : 12.5,
                        color: isCurrent ? c.blue : lv.n > 0 ? c.green : lv.n < 0 ? c.red : c.text,
                      }}>
                        {isCurrent ? 'SPX' : `${lv.n > 0 ? '+' : ''}${lv.n}σ`}
                      </td>
                      <td style={{ ...td, color: c.textSecondary }}>
                        {isCurrent ? '—' : `${lv.distance >= 0 ? '+' : ''}${fmtPrice(lv.distance)}`}
                      </td>
                      <td style={{ ...td, textAlign: 'right', color: lv.n > 0 ? c.green : c.textMuted, fontWeight: lv.n > 0 ? 600 : 400 }}>
                        {lv.n > 0 ? fmtPrice(lv.levelPrice) : isCurrent ? fmtPrice(spot) : ''}
                      </td>
                      <td style={{ ...td, color: c.textSecondary, fontSize: 12 }}>
                        {isCurrent ? '—' : `±${lv.prob.toFixed(2)}%`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Key Levels */}
          {(walls.calls.length > 0 || walls.puts.length > 0 || walls.gammaFlip != null) && (
            <div style={{ marginTop: 14, padding: 10, borderRadius: 8, backgroundColor: c.cardAlt }}>
              <div style={{ ...label, marginBottom: 8 }}>Key Levels</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, fontSize: 11 }}>
                {walls.gammaFlip != null && (
                  <span style={{ padding: '3px 8px', borderRadius: 4, backgroundColor: ds ? c.levelKeyDim : c.purpleDim, color: ds ? c.levelKey : c.purple, fontWeight: 700 }}>Γ Flip: {fmtPrice(walls.gammaFlip)}</span>
                )}
                {walls.calls.map((w, i) => (
                  <span key={`c${i}`} style={{ padding: '3px 8px', borderRadius: 4, backgroundColor: c.callWallDim, color: c.callWall, fontWeight: 700, ...wallEdge('call') }}>Call Wall: {fmtPrice(w.strike)} ({(w.oi / 1000).toFixed(0)}k)</span>
                ))}
                {walls.puts.map((w, i) => (
                  <span key={`p${i}`} style={{ padding: '3px 8px', borderRadius: 4, backgroundColor: c.putWallDim, color: c.putWall, fontWeight: 700, ...wallEdge('put') }}>Put Wall: {fmtPrice(w.strike)} ({(w.oi / 1000).toFixed(0)}k)</span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── RIGHT SIDE PANELS ─────────────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

          {/* A. Greeks per Contract */}
          <div style={card}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={label}>{isAr ? 'اليونانيات' : 'Greeks (Per Contract)'}</span>
              <span style={{ fontSize: 12, color: c.textMuted, cursor: 'default' }}>✏</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              {[
                { sym: 'Γ', name: 'Gamma', val: fmt(gamma, 6), color: c.orange, bg: c.orangeDim },
                { sym: 'Δ', name: 'Delta', val: fmt(delta, 3), color: c.blue, bg: c.blueDim },
                { sym: 'Θ', name: 'Theta', val: fmt(theta, 2), color: c.purple, bg: c.purpleDim },
                { sym: 'ν', name: 'Vega', val: fmt(vega, 2), color: c.green, bg: c.greenDim },
              ].map((g) => (ds ? { ...g, color: 'var(--text)', bg: 'var(--surface-2)' } : g)).map(({ sym, name, val, color, bg }) => (
                <div key={name} style={{ padding: '6px 8px', borderRadius: 6, border: `1px solid color-mix(in srgb, ${color} 20%, transparent)`, backgroundColor: bg }}>
                  <div style={{ fontSize: 10.5, color: c.textMuted, fontWeight: 600 }}>{sym} {name}</div>
                  <div style={{ fontSize: 15, fontWeight: 600, color, fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-mono)' }}>{val}</div>
                </div>
              ))}
            </div>
          </div>

          {/* B. Delta Change */}
          <div style={card}>
            <div style={{ ...label, marginBottom: 8 }}>{isAr ? 'تغير الدلتا' : 'Delta Change'}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 11, color: c.textSecondary }}>Move</span>
              <input
                type="number" step="any" value={moveInput || ''}
                onChange={(e) => setMoveInput(safeNum(e.target.value))}
                style={{ flex: 1, padding: '5px 8px', borderRadius: ds ? 4 : 6, border: `1px solid ${c.border}`, backgroundColor: c.cardAlt, color: c.text, fontSize: 13, fontVariantNumeric: 'tabular-nums', outline: 'none' }}
                placeholder={fmtPrice(move0dte)}
              />
              <span style={{ fontSize: 11, color: c.textMuted }}>pts</span>
            </div>
            {(() => {
              const moveSize = moveInput || move0dte;
              const d = gi(moveSize);
              return (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12 }}>
                    <span style={{ color: c.textSecondary }}>Δ Change (Γ × Move)</span>
                    <span style={{ color: c.plain || c.green, fontWeight: 700 }}>{d.deltaChange >= 0 ? '+' : ''}{fmt(d.deltaChange, 4)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12 }}>
                    <span style={{ color: c.textSecondary }}>New Delta (Est.)</span>
                    <span style={{ color: c.blue, fontWeight: 700 }}>{fmt(d.newDelta, 4)}</span>
                  </div>
                </>
              );
            })()}
          </div>

          {/* C. Gamma Impact */}
          <div style={card}>
            <div style={{ ...label, marginBottom: 8 }}>{isAr ? 'أثر الجاما' : 'Gamma Impact (Est.)'}</div>
            {(() => {
              const moveSize = moveInput || move0dte;
              const d = gi(moveSize);
              return (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12 }}>
                    <span style={{ color: c.textSecondary }}>Move</span>
                    <span style={{ color: c.text, fontWeight: 600 }}>{moveSize >= 0 ? '+' : ''}{fmtPrice(moveSize)} pts</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12 }}>
                    <span style={{ color: c.textSecondary }}>Price Impact (Est.)</span>
                    <span style={{ color: c.plain || c.green, fontWeight: 600, fontSize: 14 }}>{d.priceImpact >= 0 ? '+' : '-'}${fmt(Math.abs(d.priceImpact), 2)}</span>
                  </div>
                  <div style={{ fontSize: 10, color: c.textMuted, marginTop: 4 }}>Approx. theoretical impact</div>
                </>
              );
            })()}
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* BOTTOM: 4 Horizon Cards + Quick View                               */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr) 200px', gap: 8, marginTop: 12 }}>
        {[
          { label: '0DTE (Intraday)', move: move0dte, selected: true },
          { label: '1 Day', move: move1d, selected: false },
          { label: '1 Week (5D)', move: move1w, selected: false },
          { label: '1 Month (21D)', move: move1m, selected: false },
        ].map(({ label, move: m, selected }) => (
          <div key={label} style={{ ...card, borderBottom: selected ? `2px solid ${c.blue}` : `2px solid transparent` }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: selected ? c.blue : c.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>{label}</div>
            <div style={{ fontSize: 18, fontWeight: 600, color: c.plain || c.purple, fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-mono)' }}>±{fmtPrice(m)}</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 11 }}>
              <span style={{ color: c.green, fontWeight: 600 }}>H: {fmtPrice(spot + m)}</span>
              <span style={{ color: c.red, fontWeight: 600 }}>L: {fmtPrice(spot - m)}</span>
            </div>
          </div>
        ))}

        {/* Quick View Card */}
        <div style={{ ...card, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'relative', width: 90, height: 90, marginBottom: 8 }}>
            <ProgressRing percent={68} size={90} stroke={6} />
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: 18, fontWeight: 700, color: c.purple, fontFamily: 'var(--font-mono)' }}>68%</span>
            </div>
          </div>
          <div style={{ fontSize: 10.5, color: c.textMuted, textAlign: 'center', lineHeight: 1.4 }}>
            Price Expected<br />Within 1σ (0DTE)
          </div>
          <div style={{ fontSize: 11, fontWeight: 700, marginTop: 4, textAlign: 'center' }}>
            <span style={{ color: c.red }}>{fmtPrice(spot - move0dte)}</span>
            <span style={{ color: c.textMuted }}> – </span>
            <span style={{ color: c.green }}>{fmtPrice(spot + move0dte)}</span>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* CONSOLIDATED: Market Intelligence (replaces First Glance, Credit   */}
      {/* Hunter, Market Analytics)                                          */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 14 }}>

        {/* ── Market Regime ──────────────────────────────────────────────── */}
        <div style={card}>
          <div style={{ ...label, marginBottom: 10 }}>{isAr ? 'نظام السوق' : 'Market Regime'}</div>
          {regimeSnapshot && regime ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 28 }}>{regime.emoji}</span>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: regime.accent }}>
                    {isAr ? regime.labelAr : regime.labelEn}
                  </div>
                  <div style={{ fontSize: 11, color: c.textSecondary }}>
                    {isAr ? 'مخاطر' : 'Risk'}{' '}
                    <span style={{ color: RISK_LEVEL_COLORS[regimeSnapshot.risk_level], fontWeight: 700 }}>
                      {regimeSnapshot.risk_level}
                    </span>
                    {' · '}
                    {Math.round(regimeSnapshot.regime_confidence * 100)}% {isAr ? 'ثقة' : 'conf'}
                  </div>
                </div>
              </div>
              {regimeSnapshot.gex.trap_zone && (
                <div style={{ padding: '4px 8px', borderRadius: 4, backgroundColor: ds ? 'var(--warning-wash)' : c.redDim, color: ds ? 'var(--warning)' : c.red, fontSize: 11, fontWeight: 700 }}>
                  {isAr ? '⚠ فخ جاما' : '⚠ Gamma Trap Zone'}
                </div>
              )}
              {regimeSnapshot.gex.pin_probability != null && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                  <span style={{ color: c.textSecondary }}>{isAr ? 'احتمال التثبيت' : 'Pin Probability'}</span>
                  <span style={{ fontWeight: 700, color: c.plain || c.yellow }}>{Math.round(regimeSnapshot.gex.pin_probability * 100)}%</span>
                </div>
              )}
              {/* BL Predictors inline */}
              {rnd && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginTop: 4 }}>
                  {[
                    { l: isAr ? 'أمام BL' : 'BL Forward', v: `${rnd.forward.toFixed(2)} (${rnd.forward_pct >= 0 ? '+' : ''}${rnd.forward_pct.toFixed(2)}%)` },
                    { l: isAr ? 'الانحراف' : 'Skew', v: rnd.skewness.toFixed(2) },
                    { l: isAr ? 'التفرطح' : 'Kurtosis', v: rnd.kurtosis.toFixed(2) },
                    { l: isAr ? 'ذيل' : 'Tail Ratio', v: rnd.tail_ratio.toFixed(2) },
                  ].map(({ l, v }) => (
                    <div key={l} style={{ padding: '4px 6px', borderRadius: 4, backgroundColor: c.cardAlt, fontSize: 10 }}>
                      <div style={{ color: c.textMuted }}>{l}</div>
                      <div style={{ fontWeight: 700, color: c.text }}>{v}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: c.textMuted }}>
              {isAr ? 'تحتاج بيانات سلسلة خيارات' : 'Needs options chain data'}
            </div>
          )}
        </div>

        {/* ── Key Levels (enhanced) ──────────────────────────────────────── */}
        <div style={card}>
          <div style={{ ...label, marginBottom: 10 }}>{isAr ? 'المستويات الرئيسية' : 'Key Levels'}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {walls.gammaFlip != null && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 8px', borderRadius: 6, backgroundColor: ds ? c.levelKeyDim : c.purpleDim, ...(ds ? { borderInlineStart: '2px solid var(--level-key)' } : {}) }}>
                <span style={{ fontSize: 11, color: c.textSecondary }}>{isAr ? 'نقطة انعكاس جاما' : 'Gamma Flip'}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: ds ? c.levelKey : c.purple }}>{fmtPrice(walls.gammaFlip)}</span>
              </div>
            )}
            {walls.calls.slice(0, 3).map((w, i) => (
              <div key={`cl${i}`} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 8px', borderRadius: 6, backgroundColor: c.callWallDim, ...wallEdge('call') }}>
                <span style={{ fontSize: 11, color: c.textSecondary }}>{isAr ? 'جدار كول' : 'Call Wall'} #{i + 1}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: c.callWall }}>{fmtPrice(w.strike)} <span style={{ fontSize: 10, fontWeight: 400 }}>({(w.oi / 1000).toFixed(0)}k)</span></span>
              </div>
            ))}
            {walls.puts.slice(0, 3).map((w, i) => (
              <div key={`pl${i}`} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 8px', borderRadius: 6, backgroundColor: c.putWallDim, ...wallEdge('put') }}>
                <span style={{ fontSize: 11, color: c.textSecondary }}>{isAr ? 'جدار بوت' : 'Put Wall'} #{i + 1}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: c.putWall }}>{fmtPrice(w.strike)} <span style={{ fontSize: 10, fontWeight: 400 }}>({(w.oi / 1000).toFixed(0)}k)</span></span>
              </div>
            ))}
            {walls.calls.length === 0 && walls.puts.length === 0 && walls.gammaFlip == null && (
              <div style={{ fontSize: 12, color: c.textMuted }}>
                {isAr ? 'تحتاج بيانات OI' : 'Needs OI data from chain'}
              </div>
            )}
          </div>
        </div>

        {/* ── Credit Hunter / Trade Signals ──────────────────────────────── */}
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={label}>{isAr ? 'إشارات التداول' : 'Trade Signals'}</span>
            <button
              onClick={fetchCreditHunter}
              disabled={creditData.loading}
              style={{
                padding: '3px 10px', borderRadius: 4, border: 'none', cursor: 'pointer',
                backgroundColor: creditData.loading ? c.border : ds ? 'var(--gold)' : c.blueDim,
                color: creditData.loading ? c.textMuted : ds ? 'var(--text-on-accent)' : c.blue,
                fontSize: 10, fontWeight: 700,
              }}
            >
              {creditData.loading ? '...' : isAr ? 'مسح' : 'Scan'}
            </button>
          </div>

          {creditData.bestVertical ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: c.textSecondary, textTransform: 'uppercase' }}>
                {isAr ? 'أفضل انتشار عمودي' : 'Best Credit Vertical'}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1, padding: '6px 8px', borderRadius: 6, backgroundColor: c.redDim }}>
                  <div style={{ fontSize: 10.5, color: c.textMuted }}>{isAr ? 'بيع' : 'Short'}</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: c.red }}>{creditData.bestVertical.short != null ? fmtPrice(creditData.bestVertical.short) : '—'}</div>
                </div>
                <div style={{ flex: 1, padding: '6px 8px', borderRadius: 6, backgroundColor: c.greenDim }}>
                  <div style={{ fontSize: 10.5, color: c.textMuted }}>{isAr ? 'شراء' : 'Long'}</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: c.green }}>{creditData.bestVertical.long != null ? fmtPrice(creditData.bestVertical.long) : '—'}</div>
                </div>
              </div>
              {creditData.bestVertical.expiry && (
                <div style={{ fontSize: 11, color: c.textSecondary }}>
                  {isAr ? 'انتهاء' : 'Expiry'}: <span style={{ color: c.text, fontWeight: 600 }}>{creditData.bestVertical.expiry}</span>
                </div>
              )}
              {creditData.bestVertical.rationale && (
                <div style={{ fontSize: 11, color: c.textMuted, lineHeight: 1.5 }}>{creditData.bestVertical.rationale}</div>
              )}
              {creditData.top3.length > 0 && (
                <div style={{ marginTop: 4 }}>
                  <div style={{ fontSize: 10.5, color: c.textMuted, fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>
                    {isAr ? 'أعلى 3 ضربات' : 'Top 3 Strikes'}
                  </div>
                  {creditData.top3.map((t, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 11, borderBottom: `1px solid ${c.border}` }}>
                      <span style={{ color: c.text }}>Strike {fmtPrice(t.strike)}</span>
                      <span style={{ color: c.plain || c.yellow, fontWeight: 600 }}>Score {t.score}</span>
                    </div>
                  ))}
                </div>
              )}
              {creditData.context && (
                <div style={{ fontSize: 10, color: c.textMuted, marginTop: 4, lineHeight: 1.4 }}>{creditData.context}</div>
              )}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: c.textMuted }}>
              {creditData.loading
                ? (isAr ? 'جاري المسح...' : 'Scanning...')
                : (isAr ? 'اضغط مسح لجلب إشارات' : 'Click Scan to fetch signals')}
            </div>
          )}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* FORMULA FOOTER                                                     */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <div style={{ marginTop: 12, padding: '8px 14px', borderRadius: 8, fontSize: 10, color: c.textMuted, backgroundColor: 'var(--surface-2, rgba(139,92,246,0.05))', border: '1px solid var(--line-strong, rgba(139,92,246,0.1))' }}>
        1σ = SPX × IV × √(sessions ÷ 252) &nbsp;·&nbsp; Δ change = γ × move &nbsp;·&nbsp; Price impact = ½ × γ × (move)²
      </div>
    </div>
  );
};

export default SigmaRangeCalculator;
